import {sameCurrency} from "@/features/pricing";
import {
  ProductServiceProps,
  DeliveryTimeframeUnit,
  BookingQuestion,
  ProductServiceCurrency,
} from "./product-service.types";

/**
 * ProductService entity.
 * Encapsulates product service data and business rules.
 *
 * Pure TypeScript - no React imports.
 */
export class ProductService {
  private readonly props: ProductServiceProps;

  private constructor(props: ProductServiceProps) {
    this.props = props;
  }

  /**
   * Creates a ProductService entity with validation.
   * @throws Error if required fields are missing or invalid
   */
  static create(props: ProductServiceProps): ProductService {
    if (props.id === undefined || props.id === null) {
      throw new Error("ProductService id is required");
    }

    if (!props.name || !props.name.trim()) {
      throw new Error("ProductService name is required");
    }

    if (props.deliveryTimeframeValue <= 0) {
      throw new Error("Delivery timeframe value must be positive");
    }

    return new ProductService(props);
  }

  // ==================== Getters ====================

  get id(): number {
    return this.props.id;
  }

  get userId(): number {
    return this.props.userId;
  }

  get name(): string {
    return this.props.name;
  }

  get slug(): string {
    return this.props.slug;
  }

  get tinyUrl(): string {
    return this.props.tinyUrl;
  }

  get description(): string {
    return this.props.description;
  }

  get deliveryTimeframeUnit(): DeliveryTimeframeUnit {
    return this.props.deliveryTimeframeUnit;
  }

  get deliveryTimeframeValue(): number {
    return this.props.deliveryTimeframeValue;
  }

  get hideFromProfile(): boolean {
    return this.props.hideFromProfile;
  }

  get urlRedirect(): string | null {
    return this.props.urlRedirect;
  }

  get redirectType(): "none" | "telegram" | "url" | undefined {
    return this.props.redirectType;
  }

  get telegramGroupChatId(): string | null | undefined {
    return this.props.telegramGroupChatId;
  }

  get statusId(): number {
    return this.props.statusId;
  }

  get requestPhoneNumber(): boolean {
    return this.props.requestPhoneNumber;
  }

  get bookingQuestions(): BookingQuestion[] {
    return this.props.bookingQuestions;
  }

  get coverImages(): string[] {
    return this.props.coverImages;
  }

  get callPrice(): number {
    return this.props.callPrice;
  }

  get callPriceCurrency(): string | null {
    return this.props.callPriceCurrency;
  }

  get enableCurrencyConversion(): boolean {
    return this.props.enableCurrencyConversion;
  }

  get currencies(): ProductServiceCurrency[] {
    return this.props.currencies;
  }

  /**
   * Whether the customer names their own price. The only Pricing Model the wire states
   * outright — Free and Single Fee stay derived from the amount.
   */
  get isPayWhatYouWant(): boolean {
    return this.props.isPayWhatYouWant;
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get updatedAt(): string {
    return this.props.updatedAt;
  }

  // ==================== Business Methods ====================

  /**
   * Get formatted delivery timeframe string.
   * Returns "{value} {unit}" (e.g., "3 days", "1 week").
   */
  getDeliveryTimeframeDisplay(): string {
    const value = this.props.deliveryTimeframeValue;
    const unit = this.props.deliveryTimeframeUnit;

    // Handle singular vs plural
    if (value === 1) {
      // Remove trailing 's' for singular (days -> day, weeks -> week)
      return `${value} ${unit.slice(0, -1)}`;
    }
    return `${value} ${unit}`;
  }

  /**
   * Check if the product service is visible on the profile.
   */
  isVisibleOnProfile(): boolean {
    return !this.props.hideFromProfile;
  }

  /**
   * Check if the product service has booking questions.
   */
  hasBookingQuestions(): boolean {
    return this.props.bookingQuestions.length > 0;
  }

  /**
   * Get the base currency (primary pricing currency).
   *
   * Matched by currency code against `callPriceCurrency`, not by the row's `isBase`.
   * The contract declares `isBase` required on every currency row, but the live Product
   * Service response omits the key entirely — so a `find` on it always came back empty,
   * and the edit form reloaded with no row marked base.
   */
  getBaseCurrency(): ProductServiceCurrency | undefined {
    return (
      this.props.currencies.find((c) =>
        sameCurrency(c.currency, this.props.callPriceCurrency)
      ) ??
      // A response that does carry `isBase` is still believed; only its absence is
      // worked around.
      this.props.currencies.find((c) => c.isBase)
    );
  }

  /**
   * Returns the raw properties for serialization or mapping.
   */
  toProps(): ProductServiceProps {
    return {...this.props};
  }
}
